import type { TranslationCatalog } from "../types.js";

function coinWord(n: number): string {
  const mod100 = Math.abs(n) % 100;
  if (mod100 === 1) return "novčič";
  if (mod100 === 2) return "novčiča";
  if (mod100 === 3 || mod100 === 4) return "novčiči";
  return "novčičev";
}

export default {
  "common.loading": "Povezovanje...",
  "common.loadingEllipsis": "Nalaganje...",
  "common.unknownError": "Neznana napaka",
  "common.errorPrefix": (p: { message: string }) => `Napaka: ${p.message}`,
  "common.retry": "Poskusi znova",
  "common.close": "Zapri",
  "common.back": "← Nazaj",
  "common.cancel": "Prekliči",
  "common.details": "Podrobnosti",
  "common.settingsTitle": "⚙️ Nastavitve",
  "common.profile": "👤 Profil",
  "common.rules": "📖 Pravila",

  "login.subtitle": "Prijavi se za igranje.",
  "login.continueFacebook": "Nadaljuj s Facebook računom",
  "login.continueGoogle": "Nadaljuj z Google računom",
  "login.continueEmail": "Nadaljuj z e-poštnim naslovom",
  "login.tagline": "Igra s kartami z aduti in licitiranjem za tri igralce. Licitiraj, izberi aduta in poberi vzetke — kdor prvi doseže 101 točko, zmaga.",
  "login.linkRules": "Pravila",
  "login.linkStrategy": "Strategija",
  "login.linkAbout": "O igri",
  "login.linkPrivacy": "Zasebnost",

  "lobby.entryFee": (p: { fee: number }) => `Vstopnina na partijo: ${p.fee} ${coinWord(p.fee)}`,
  "lobby.resumeMatch": "Nadaljuj partijo",
  "lobby.playButton": "Igraj (3 igralci)",
  "lobby.inviteFriend": "📣 Povabi prijatelja",
  "lobby.insufficientFunds": (p: { missing: number }) => `Nimaš dovolj novčičev za vstopnino (manjka ${p.missing}).`,
  "lobby.watchAd": "Poglej oglas za dodatne novčiče",

  "leaderboard.title": "🏆 Lestvica",
  "leaderboard.emptySeason": "To sezono še ni uvrščenih igralcev (potrebna je vsaj 1 odigrana partija).",
  "leaderboard.emptyGlobal": "Še ni uvrščenih igralcev (potrebna je vsaj 1 odigrana partija skupno).",
  "leaderboard.seasonTabDefault": "Sezona",
  "leaderboard.seasonTabWithDate": (p: { month: string; year: string }) => `Sezona ${p.month}/${p.year}`,
  "leaderboard.globalTab": "🌍 Skupno (vse sezone)",
  "leaderboard.colPlayer": "Igralec",
  "leaderboard.colPoints": "Točke",
  "leaderboard.colMatches": "Partije",
  "leaderboard.you": "Ti",
  "leaderboard.yourScoreUnranked": (p: { points: number; matchesPlayed: number }) =>
    `Tvoj rezultat: ${p.points} točk iz ${p.matchesPlayed} partij (za uvrstitev je potrebna vsaj 1 partija)`,

  "rules.title": "📖 Kako se igra Meksiko",
  "rules.cardsHeading": "Karte",
  "rules.cardsBody":
    "Igra se z 32 kartami (7, 8, 9, 10, J, Q, K, A v vseh 4 barvah). Moč kart znotraj barve, od najšibkejše: 7 &lt; 8 &lt; 9 &lt; 10 &lt; J &lt; Q &lt; K &lt; A. Adutska barva premaga vse neadutske karte, ne glede na vrednost.",
  "rules.dealingHeading": "Deljenje",
  "rules.dealingBody": 'Vsak od 3 igralcev dobi 10 kart (v dveh krogih po 5), 2 karti pa ostaneta skriti v "talonu".',
  "rules.biddingHeading": "Licitacija",
  "rules.biddingBody":
    'Prvi igralec mora licitirati vsaj 5. Vsak naslednji mora licitirati strogo višje (5, 6, 7, 8, 9, 10, ali "MEKSIKO") ali reči "naprej" — kdor enkrat reče "naprej", trajno izpade iz licitacije za to deljenje. Licitacija se konča, ko ostane samo en igralec.',
  "rules.talonHeading": "Talon in odmet",
  "rules.talonBody":
    "Zmagovalec licitacije odpre talon (2 karti, vidni vsem), ju doda svoji roki (zdaj ima 12 kart), nato odvrže 2 karti s hrbtno stranjo navzgor. Nato izbere adutsko barvo.",
  "rules.mexicoHeading": "MEKSIKO (20)",
  "rules.mexicoBody":
    'Kdor licitira "MEKSIKO", se zaveže, da bo dobil vseh 10 vzetkov. Igra se brez aduta in brez talona — samo z originalnimi 10 kartami.',
  "rules.tricksHeading": "Potek igre (vzetki)",
  "rules.tricksBody":
    "Slediti moraš izigrani barvi, če jo imaš. Če je nimaš, moraš vreči aduta, če ga imaš (rezanje) — le če nimaš ničesar od tega, lahko vržeš karkoli. Vzetek dobi najmočnejša karta izigrane barve, razen če je nekdo vrgel aduta — takrat dobi najmočnejši adut, ne glede na barvo. Kdor dobi vzetek, začne naslednjega.",
  "rules.scoringHeading": "Točkovanje",
  "rules.scoringBody":
    "Zmagovalec licitacije: če dobi vsaj toliko vzetkov, kolikor je licitiral, dobi toliko točk, kolikor jih je dejansko dobil. Če mu ne uspe, dobi <strong>negativne</strong> točke v višini licitacije. Ostala dva igralca vedno dobita točke, enake številu vzetkov, ki sta jih dobila.",
  "rules.matchEndHeading": "Konec partije",
  "rules.matchEndBody":
    "Partija se konča, ko nekdo doseže ciljni rezultat. Če več igralcev preseže cilj v istem deljenju z enakim najvišjim rezultatom, se igra dodatno deljenje, dokler se ne razreši.",

  "settings.soundOff": "🔇 Zvok izklopljen",
  "settings.soundOn": "🔊 Zvok vklopljen",
  "settings.displayNameLabel": "Ime v igri",
  "settings.saveDisplayName": "Shrani ime",
  "settings.displayNameLengthError": "Ime mora imeti 1-20 znakov.",
  "settings.saveFailed": "Shranjevanje ni uspelo, poskusi znova.",
  "settings.logout": "🚪 Odjava",

  "waitingRoom.waitingForPlayers": (p: { filled: number; total: number }) => `Čakanje na igralce... ${p.filled}/${p.total}`,
  "waitingRoom.countdown": (p: { remainingSec: number }) => `Še ${p.remainingSec}s, preden AI zapolni prazna mesta`,

  "game.passLabel": "naprej",
  "game.passButton": "Naprej",
  "game.mexicoLabel": "MEKSIKO",
  "game.bidLabel": (p: { bid: string }) => `Licitacija: ${p.bid}`,
  "game.trumpLabel": (p: { suit: string }) => `Adut: ${p.suit}`,
  "game.tricksWonLabel": (p: { count: number }) => `Vzetki: ${p.count}`,
  "game.biddingInProgress": "Licitacija poteka...",
  "game.yourBid": "Tvoja licitacija",
  "game.someoneDiscarding": (p: { name: string }) => `${p.name} odmetava karte...`,
  "game.someoneChoosingTrump": (p: { name: string }) => `${p.name} izbira aduta...`,
  "game.chooseTrumpPrompt": "Izberi aduta",
  "game.nextDealPause": "Naslednje deljenje...",
  "game.fromTalon": "iz talona",
  "game.talonRevealLabel": (p: { bidder: string }) => `Karte iz talona (${p.bidder})`,
  "game.yourTurnLabel": "Ti si na vrsti",
  "game.turnTimerLabel": (p: { name: string; remainingSec: number }) => `${p.name} — ${p.remainingSec}s`,
  "game.idleBotTakeover": "🤖 Poteza ni bila odigrana pravočasno, zato zdaj namesto tebe igra AI. Klikni kjerkoli, da prevzameš nadzor.",
  "game.leaveMatch": "Zapusti partijo",
  "game.cardAlt": "karta",
  "game.botDisplayName": "AI igralec",
  "game.botBadgeIdle": "🤖 AI (začasno)",
  "game.botBadgeFull": "🤖 BOT",
  "game.declarerTooltip": "Vodi licitacijo",

  "matchEnd.title": "Partija končana",
  "matchEnd.winner": (p: { name: string }) => `Zmagovalec: ${p.name}`,
  "matchEnd.backToLobby": "Nazaj v lobi",

  "reactions.sendButton": "Pošlji reakcijo",
  "reaction.angry_small_card": "Zakaj z majhno karto?",
  "reaction.angry_big_card": "Zakaj z veliko karto?",
  "reaction.bravo": "Bravo",
  "reaction.svaka_cast": "Klobuk dol",
  "reaction.cestitam_pobeda": "Čestitke za zmago",
  "reaction.izvini": "Oprosti",
  "reaction.pa_zasto": "Pa zakaj!",
  "reaction.srecan": "",
  "reaction.srce": "",
  "reaction.pozdrav": "",

  "medal.season_gold": "🥇 Sezona",
  "medal.season_silver": "🥈 Sezona",
  "medal.season_bronze": "🥉 Sezona",
  "medal.first_match": "🎉 Prva partija",
  "medal.matches_100": "💯 100 partij",
  "medal.mexico_master": "🌵 Mojster Meksika",
  "medal.hat_trick_3_wins": "🔥 Hat-trick (3 zmage)",
  "medal.tricks_1000": "🃏 1000 vzetkov",

  "suit.diamonds": "Karo",
  "suit.hearts": "Srce",
  "suit.clubs": "Tref",
  "suit.leaves": "List",

  "profile.matchesPlayed": "Odigranih partij",
  "profile.firstPlace": (p: { wins: number }) => `🥇 1. mesto (${p.wins})`,
  "profile.secondPlace": (p: { count: number }) => `🥈 2. mesto (${p.count})`,
  "profile.thirdPlace": (p: { count: number }) => `🥉 3. mesto (${p.count})`,
  "profile.pointsConfigNote": (p: { win: number; second: number; third: number }) =>
    `Točkovanje na partijo: 1. mesto ${p.win}, 2. mesto ${p.second}, 3. mesto ${p.third} t.`,
  "profile.noMedals": "Še ni osvojenih medalj.",
  "profile.medalsHeading": "Medalje",
  "profile.seasonHistoryHeading": "Zgodovina sezon",
  "profile.colSeason": "Sezona",
  "profile.colRank": "Uvrstitev",
  "profile.deleteAccountButton": "🗑️ Izbriši račun",
  "profile.deleteAccountTitle": "Izbriši račun",
  "profile.deleteAccountWarning":
    "To trajno izbriše tvoj račun in vse podatke (zgodovino partij, lestvico, medalje, stanje novčičev). Tega dejanja ni mogoče razveljaviti.",
  "profile.deleteAccountConfirm": "Da, izbriši račun",
  "profile.deleteAccountFailed": "Brisanje ni uspelo, poskusi znova.",
  "profile.deleteAccountActiveMatch": "Računa ne moreš izbrisati, dokler si v aktivni partiji. Končaj ali zapusti partijo in poskusi znova.",

  "errors.NOT_YOUR_TURN": "Nisi na vrsti.",
  "errors.INVALID_BID": "Neveljavna licitacija.",
  "errors.BID_TOO_LOW": "Licitacija mora biti višja od trenutno najvišje.",
  "errors.MUST_BID_FIRST": "Prvi igralec mora licitirati vsaj 5, ne more reči naprej.",
  "errors.DUPLICATE_DISCARD": "Iste karte ne moreš odvreči dvakrat.",
  "errors.CARD_NOT_IN_HAND": "Te karte nimaš v roki.",
  "errors.MUST_FOLLOW_SUIT": "Slediti moraš barvi ali rezati z adutom, če ne moreš.",
  "errors.TRICK_INCOMPLETE": "Vzetek še ni zaključen.",
  "errors.MATCH_ENDED": "Partija je že končana.",
  "errors.UNKNOWN_ACTION": "Neznano dejanje.",
  "errors.WRONG_PHASE": "To dejanje v tej fazi igre ni dovoljeno.",
  "errors.NOT_BID_WINNER": "To lahko naredi samo zmagovalec licitacije.",
  "errors.MATCH_NOT_ENDED": "Partija še ni končana.",
  "errors.invalid_session_token": "Seja je potekla, prijavi se znova.",
  "errors.room_full": "Miza je polna, poskusi znova čez trenutek.",
  "errors.insufficient_balance": "Nimaš dovolj novčičev za vstopnino.",
  "errors.ad_unavailable": "Oglas trenutno ni na voljo. Preveri internetno povezavo (ali izklopi AdBlock) in poskusi znova.",
  "errors.invalid_or_early_ticket": "Oglas ni bil prepoznan kot ogledan. Poskusi znova.",
  "errors.daily_ad_limit_reached": "Dosegel si dnevno omejitev oglasov. Poskusi jutri.",

  "share.inviteText": "Igraj Meksiko z mano!",
  "ad.placeholderText": "Simuliran oglas (standalone spletna demo različica)",
  "rotateOverlay.text": "Obrni telefon, da igraš Meksiko",
  "languageSwitcher.ariaLabel": "Jezik",
} satisfies TranslationCatalog;
